import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Material UI
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Zoom from '@mui/material/Zoom';
import DialogActions from '@mui/material/DialogActions'

// Components
import Dropdown from '../../../components/inputs/dropdown';
import Modal from "../../../components/modal/index";
import AutocompleteSubChannels from '../../../components/inputs/autocompletes/autocompleteSubChannels';

// Hooks
import useUser from '../../../hooks/user/useUser';
import useNotify from "../../../hooks/notify";

import { currencies, defaultCurrencyByCountry } from '../../../models/EnumEntity';
import { channels, subChannels } from '../../../models/Channels';
import SellerAutocomplete from '../../../components/sellerAutocomplete';

import { utils } from '../../../utils/utils';
import orderService from '../../../services/orderService';
import Spinner from '../../../components/spinner';

const Transition = React.forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const ModalNewSale = ({ open, onClose, orderId, refundItems, manualInvoiceNumber }) => {
    const { branch, pv, cashRegister, business } = useUser();
    const { Notify, variants } = useNotify();
    const push = useNavigate();

    const [channelSelected, setChannelSelected] = useState(subChannels.values.POS);
    const [billingCodeSelected, setBillingCodeSelected] = useState(0);
    const [sellerSelected, setSellerSelected] = useState();
    const [loading, setLoading] = useState(false);

    // Callbacks
    const getPvs = useCallback(() => {
        if (!cashRegister || !branch)
            return [];

        return cashRegister.associatePvs
            .filter(apv => apv.subChannel === channelSelected)
            .map(apv => {
                const pv = branch.pvs
                    .find(p => p.billingCode === apv.billingCode);

                return { ...apv, name: pv?.name || '' }
            })
    }, [branch, cashRegister, channelSelected])

    useEffect(() => {
        if (channelSelected) {
            const pvs = getPvs();
            const billingCode = channelSelected === subChannels.values.POS
                ? pv.billingCode
                : pvs.length > 0
                    ? pvs[0].billingCode
                    : '';

            setBillingCodeSelected(billingCode);

            if (utils.validateStringNullOrEmpty(billingCode))
                Notify("La caja no tiene ningún pv asociado sobre el canal seleccionado.", variants.ERROR);
        }

        return () => setLoading(false);
    }, [channelSelected, pv, Notify, variants.ERROR, getPvs])

    const onSaveOrder = async () => {
        if (!isValid())
            return;

        const req = {
            channel: business.isFranchise ? channels.values.Franchises : channels.values.Tienda,
            subChannel: channelSelected,
            sellerCode: sellerSelected.code,
            billingCode: Number(billingCodeSelected),
            country: branch.country,
            currency: defaultCurrencyByCountry[branch.country] ?? currencies[0].value,
            branchCode: branch.code,
            warehouseId: branch.warehouseId,
            refundItems,
            manualInvoiceNumber,
            businessId: business.id,
        };

        setLoading(true);
        const res = !orderId
            ? await orderService.Create(req)
            : await orderService.CreateChange(orderId, req);

        push(`/orders/${res.id}`);
        onClose();
    }

    const isValid = () => {
        if (!channelSelected)
            return false

        if (!sellerSelected?.code)
            return false

        if (!billingCodeSelected)
            return false

        return true;
    }

    const handleSubChannelChange = (event, option) =>
        setChannelSelected(option?.value)

    return (
        <>
            {loading && <Spinner lockScreen message="Creando venta" />}
            <Modal
                fullWidth
                open={open}
                onClose={onClose}
                title={`${orderId ? 'Cambio' : 'Nueva venta'} - ${branch.name}`}
                TransitionComponent={Transition}
                contentProps={{ dividers: true }}
                customButtons={() =>
                    <DialogActions>
                        <Button
                            color='primary'
                            variant='outlined'
                            onClick={onClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            color='primary'
                            variant='contained'
                            disabled={!isValid() || loading}
                            onClick={onSaveOrder}
                        >
                            Aceptar
                        </Button>
                    </DialogActions>
                }
            >
                <Grid container spacing={1}>
                    <Grid size={6}>
                        <AutocompleteSubChannels
                            required
                            disableClearable
                            channel={channels.values.Tienda}
                            onChange={handleSubChannelChange}
                            value={channelSelected || ""}
                            noShow={[subChannels.values.AppInStore]}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Dropdown
                            fullWidth
                            required
                            name='billingCode'
                            label='Punto de Venta'
                            disabled={channelSelected === subChannels.values.POS || utils.validateStringNullOrEmpty(billingCodeSelected)}
                            getOptionLabel={(option) => option?.name || ''}
                            getOptionValue={(option) => option?.billingCode || ''}
                            onChange={(e) => setBillingCodeSelected(e.target.value)}
                            value={billingCodeSelected}
                            values={getPvs()}
                        />
                    </Grid>
                    <Grid size={12}>
                        <SellerAutocomplete
                            onChange={setSellerSelected}
                            value={sellerSelected}
                        />
                    </Grid>
                </Grid>
            </Modal>
        </>
    );
}

export default ModalNewSale;